/**
 * Community Live Notification Component
 * Displays a notification when a community is created
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"
import { CommunityNotificationCard } from "./CommunityNotificationCard"

interface CommunityLiveNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
}

interface CommunityLiveNotificationData {
  communityId?: string
  communityName?: string
  communityCoverImageUrl?: string | null
  city?: string | null
  state?: string | null
}

export function CommunityLiveNotification({
  notification,
  onRead,
  onUnread,
}: CommunityLiveNotificationProps) {
  const data = notification.data as CommunityLiveNotificationData

  const communityId = data.communityId
  const communityName = data.communityName || "a community"
  const communityCoverImageUrl = data.communityCoverImageUrl
  const city = data.city
  const state = data.state
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const location = city && state ? `${city}, ${state}` : city ? city : state ? state : null

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/communities/${communityId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              Your community {communityName} is now live. Invite people to join the circle.
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CommunityNotificationCard
        coverImageUrl={communityCoverImageUrl}
        title={communityName}
        location={location}
        onClick={handleCommunityClick}
      />
    </div>
  )
}
